/**
 * Submissions Data Layer
 *
 * TypeScript types, React Query hooks, and edge function helpers
 * for the investor submission inbox feature.
 *
 * Submission links allow investors to receive pitch decks from startups
 * via a public URL. Submissions are the pitch decks received through those links.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { queryKeys } from './api';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import type { AuthUserId } from '@/lib/ids';
import { fetchInvestorScoreEvents } from './investor-score-events';
import {
  buildCreateLinkBody,
  buildUpdateLinkBody,
  unwrapCreateResponse,
  unwrapListResponse,
  type SubmissionLink as SubmissionLinkContract,
  type CreateLinkEnvelope,
  type ListLinksEnvelope,
} from './submission-link-contract';
// NOTE: the dealflow row shape used to be built here. It now lives at
// `supabase/functions/_shared/dealflow/shortlist-payload.ts`, because the
// edge function is the only writer — one definition of one row shape.

// The 'submissions' and 'submission_links' tables were added in Phase 70
// but database.types.ts has not been regenerated yet. We cast to `any`
// for .from() calls targeting these tables until types are refreshed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ============================================================================
// Types
// ============================================================================

// Re-export SubmissionLink from the contract module so the type lives in
// exactly one place. Other modules continue to import it from this file.
export type SubmissionLink = SubmissionLinkContract;

export interface SubmissionExtractedData {
  sector: string | null;
  stage: string | null;
  one_liner: string | null;
  ask_amount: number | null;
  team_size: number | null;
  /**
   * 119-03: the deck matches a company that has CLOSED on Inverio. The deck is
   * held — not scored, not linked — until the investor decides whether it is a
   * different venture (`confirm-different-company`) or dismisses it (archive).
   * Public card fields only: the closed company's name and lifecycle status.
   */
  matchedClosedCompany?: { name: string; status: string } | null;
  /** 119-03: set once the investor confirmed it is a different company. */
  confirmedNotClosedCompany?: unknown;
}

export type SubmissionStatus =
  | 'pending'
  | 'scoring'
  | 'scored'
  | 'shortlisted'
  | 'archived'
  | 'rejected'
  /** Deck genuinely could not be scored (unreadable PDF / insufficient info).
   *  Honest pending-style state — never carries a fabricated number. */
  | 'unscorable';

// NOTE: `invited` and `claimed` are NOT statuses. They are informational tags
// derived from structured data that already exists — `submission_invites`
// (invite sent) and `submissions.claimed_by` (founder claimed their profile) —
// and are surfaced via `submissionTags()` below. They used to be written into
// `status`, which clobbered the real scoring status (a scored deck that got
// claimed lost its "scored" state). The write-paths no longer do that.

// ----------------------------------------------------------------------------
// Held for a closed-company decision (119-03 / 119-07)
// ----------------------------------------------------------------------------

/** The closed company a held deck matches, as the server stored it. */
export interface MatchedClosedCompany {
  name: string;
  /** closed | acquired | bankrupt (anything else reads as closed). */
  status: 'closed' | 'acquired' | 'bankrupt';
}

function asMatchedClosedCompany(v: unknown): MatchedClosedCompany | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  if (typeof r.name !== 'string' || !r.name.trim()) return null;
  const status = r.status === 'acquired' || r.status === 'bankrupt' ? r.status : 'closed';
  return { name: r.name, status };
}

/**
 * The closed company this submission is HELD for, or null.
 *
 * Mirrors the server's own check (submission-action `confirm-different-company`):
 * the marker is present, nothing was scored and no profile was linked. A deck the
 * investor already confirmed has the marker removed and is not held.
 */
export function heldForClosedCompany(
  sub: Pick<Submission, 'ai_extracted_data' | 'ai_score' | 'startup_profile_id'>,
): MatchedClosedCompany | null {
  if (sub.ai_score != null || sub.startup_profile_id) return null;
  return asMatchedClosedCompany(sub.ai_extracted_data?.matchedClosedCompany);
}

/** "This deck matches Acme AG, a company listed as acquired on Inverio. Is this a different venture?" */
export function heldDeckPrompt(company: MatchedClosedCompany): string {
  return `This deck matches ${company.name}, a company listed as ${company.status} on Inverio. Is this a different venture?`;
}

export const SUBMISSION_COMPANY_CLOSED_COPY = 'This company has closed. Its submissions are read-only.';
const SUBMISSION_ACTION_GENERIC = 'This action could not be completed. Please try again.';

export type SubmissionActionErrorKind = 'matched_closed_company' | 'company_closed' | 'not_held_for_review' | 'failed';

/** A submission-action refusal, branched on its `code` — never on message text. */
export class SubmissionActionError extends Error {
  readonly kind: SubmissionActionErrorKind;
  readonly userMessage: string;
  readonly status?: number;
  /** matched_closed_company: the closed company the deck matches. */
  readonly company: MatchedClosedCompany | null;

  constructor(kind: SubmissionActionErrorKind, userMessage: string, opts: { status?: number; company?: MatchedClosedCompany | null; detail?: string } = {}) {
    super(`submission_action_${kind}${opts.detail ? `: ${opts.detail}` : ''}`);
    this.name = 'SubmissionActionError';
    this.kind = kind;
    this.userMessage = userMessage;
    this.status = opts.status;
    this.company = opts.company ?? null;
  }
}

/** Map a non-2xx status + body to the typed error. Pure; exported for tests. */
export function mapSubmissionActionRefusal(status: number, body: Record<string, unknown>): SubmissionActionError {
  const code = typeof body.code === 'string' ? body.code : null;
  if (code === 'matched_closed_company') {
    const company = asMatchedClosedCompany(body.company);
    return new SubmissionActionError(
      'matched_closed_company',
      company ? heldDeckPrompt(company) : 'This deck matches a company that has closed on Inverio. Is this a different venture?',
      { status, company, detail: code },
    );
  }
  if (code === 'company_closed') {
    return new SubmissionActionError('company_closed', SUBMISSION_COMPANY_CLOSED_COPY, { status, detail: code });
  }
  if (code === 'not_held_for_review') {
    return new SubmissionActionError('not_held_for_review', 'This submission is no longer waiting for a decision.', { status, detail: code });
  }
  return new SubmissionActionError('failed', SUBMISSION_ACTION_GENERIC, { status, detail: code ?? `http_${status}` });
}

/**
 * functions.invoke puts a non-2xx body on `error.context` — the only place the
 * `code` (and the matched company) survives. Network failures have no status.
 */
async function submissionActionError(error: unknown, action: string): Promise<SubmissionActionError> {
  const err = error as { name?: unknown; message?: unknown; context?: { status?: unknown; json?: () => Promise<unknown> } };
  if (err?.name !== 'FunctionsHttpError' || typeof err.context?.status !== 'number') {
    return new SubmissionActionError('failed', SUBMISSION_ACTION_GENERIC, {
      detail: typeof err?.message === 'string' ? err.message : String(error),
    });
  }
  let body: Record<string, unknown> = {};
  try {
    const parsed = await err.context.json?.();
    body = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch (parseErr) {
    reportError(parseErr, { context: `submissionAction.${action}.errorBody`, level: 'warning' });
  }
  return mapSubmissionActionRefusal(err.context.status, body);
}

/** An informational tag on a submission (independent of its scoring status). */
export interface SubmissionTag {
  key: 'claimed' | 'invited';
  label: string;
}

/**
 * Derive the informational tags for a submission from existing structured data.
 * `claimed` ← `claimed_by` is set (founder claimed their profile);
 * `invited`  ← a `claim_page` invite row exists (investor invited them to claim).
 */
export function submissionTags(s: Submission): SubmissionTag[] {
  const tags: SubmissionTag[] = [];
  if (s.claimed_by) tags.push({ key: 'claimed', label: 'Claimed' });
  if (s.submission_invites?.some((i) => i.invite_type === 'claim_page')) {
    tags.push({ key: 'invited', label: 'Invited' });
  }
  return tags;
}

export interface Submission {
  id: string;
  link_id: string;
  investor_user_id: string;
  company_name: string;
  contact_email: string;
  contact_name: string | null;
  /** Optional 400-char context from the submitter (Phase 116.1). */
  submitter_note?: string | null;
  deck_storage_path: string | null;
  deck_size_bytes: number;
  ai_score: number | null;
  ai_confidence: number | null;
  ai_extracted_data: SubmissionExtractedData | null;
  /** Promoted startup_profiles row — set once scored, links to the canonical
   *  score_event (composite, dimensions, risks). NULL when unscorable. */
  startup_profile_id: string | null;
  status: SubmissionStatus;
  /** Set when the founder has claimed their profile from this submission.
   *  Drives the "Claimed" TAG (independent of scoring status). */
  claimed_by: string | null;
  /** Embedded claim/request invites (from the list query) — drives the
   *  "Invited" TAG. See submissionTags(). */
  submission_invites?: Array<{ invite_type: string }> | null;
  /**
   * Embedded directory row, for the company logo.
   *
   * An ARRAY because PostgREST only collapses a reverse embed to an object
   * when it can prove the relation is to-one, and
   * `company_directory_source_submission_uniq` is a PARTIAL unique index —
   * which it does not treat as proof. There is at most one element; that is
   * exactly what the index guarantees. Read it via `submissionLogoUrl()`.
   */
  company_directory?: Array<{ logo_url: string | null; logo_is_knockout?: boolean | null }> | null;
  score_visible_to_marketplace: boolean;
  consent_given: boolean;
  created_at: string;
  scored_at: string | null;
  expires_at: string | null;
}

/**
 * The logo of the company a submission produced, if one was sourced.
 *
 * Logos are extracted from the deck and re-hosted, but they live on
 * `company_directory` — so the submissions inbox rendered initials for
 * companies whose logo was already stored and served.
 */
export function submissionLogoUrl(submission: Submission): string | null {
  return submission.company_directory?.[0]?.logo_url ?? null;
}

/**
 * Whether that logo is a near-white knockout needing a dark chip.
 *
 * Paired with `submissionLogoUrl` on purpose: a caller that takes the URL and
 * not this renders a white wordmark on a light card, i.e. nothing.
 */
export function submissionLogoIsKnockout(submission: Submission): boolean {
  return submission.company_directory?.[0]?.logo_is_knockout === true;
}

// ============================================================================
// Edge Function Helper — submission-link
// ============================================================================

/**
 * Call the submission-link edge function with a specific HTTP method.
 *
 * The edge function routes by HTTP method (GET, POST, PATCH, DELETE).
 * Since supabase.functions.invoke() always uses POST, we use raw fetch
 * with configurable method, following the callEdgeFunction pattern.
 */
async function callSubmissionLinkAPI<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  params?: Record<string, string>,
  body?: object,
): Promise<{ data: T | null; error: string | null }> {
  // Mock mode — return mock data
  if (mode.isMock) {
    return { data: null, error: null };
  }

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: 'Not authenticated. Please log in again.' };
    }

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

    let url = `${supabaseUrl}/functions/v1/submission-link`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'apikey': anonKey,
      },
      signal: controller.signal,
    };

    if (body && (method === 'POST' || method === 'PATCH')) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    const responseData = await response.json();

    if (!response.ok) {
      const errorMsg =
        responseData?.error ||
        responseData?.message ||
        `submission-link ${method} failed (HTTP ${response.status})`;
      return { data: null, error: errorMsg };
    }

    return { data: responseData as T, error: null };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { data: null, error: 'Request timed out. Please try again.' };
    }
    reportError(err, { context: `callSubmissionLinkAPI:${method}`, silent: true });
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

// ============================================================================
// React Query Hooks — Queries
// ============================================================================

/**
 * Fetch all submission links for an investor
 */
export function useSubmissionLinks(investorId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.submissionLinks(investorId || ''),
    queryFn: async () => {
      if (!investorId) return [];

      if (mode.isMock) {
        return [getMockSubmissionLink()];
      }

      // Edge function returns `{ success: true, links: [...] }` envelope; the
      // hook is the consumer-facing surface so it unwraps. See
      // submission-link-contract.ts for the schema this mirrors.
      const { data, error } = await callSubmissionLinkAPI<ListLinksEnvelope>(
        'GET',
        { investor_user_id: investorId },
      );

      if (error) throw new Error(error);
      return unwrapListResponse(data);
    },
    enabled: !!investorId,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

/**
 * Fetch all submissions for an investor
 */
/**
 * Submissions for an investor. Takes the AUTH user id.
 *
 * `submissions.investor_user_id` references auth.users(id), NOT
 * investor_profiles(id) — unlike conversations and dealflow, which sit in
 * the other space under the same column name. Branded so the two cannot be
 * confused: passing a profile id here returns an empty list with no error,
 * which is exactly how the investor dashboard's Inbox badge silently read 0.
 */
export function useSubmissions(investorId: AuthUserId | undefined) {
  return useQuery({
    queryKey: queryKeys.submissions(investorId || ''),
    queryFn: async () => {
      if (!investorId) return [];

      if (mode.isMock) {
        return getMockSubmissions();
      }

      if (!isSupabaseConfigured()) return [];

      const { data, error } = await db
        .from('submissions')
        .select('*, submission_invites(invite_type), company_directory(logo_url, logo_is_knockout)')
        .eq('investor_user_id', investorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as unknown as Submission[]) || [];
    },
    enabled: !!investorId,
    staleTime: 2 * 60 * 1000, // 2 min
    // A submission lands as `scoring` and flips to `scored` ~25s later (the deck
    // is scored in a background task). Poll while anything is still in flight so
    // the row updates without a manual reload, and stop once everything settles —
    // no perpetual polling. Also refetch when the investor refocuses the app.
    refetchInterval: (query) => {
      const rows = query.state.data as Submission[] | undefined;
      const inFlight = rows?.some(
        (s) => s.status === 'scoring' || s.status === 'pending',
      );
      return inFlight ? 8000 : false;
    },
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch a single submission by ID
 */
export function useSubmissionDetail(submissionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.submissionDetail(submissionId || ''),
    queryFn: async () => {
      if (!submissionId) return null;

      if (mode.isMock) {
        const mocks = getMockSubmissions();
        return mocks.find((s) => s.id === submissionId) ?? mocks[0] ?? null;
      }

      if (!isSupabaseConfigured()) return null;

      const { data, error } = await db
        .from('submissions')
        .select('*, company_directory(logo_url, logo_is_knockout)')
        .eq('id', submissionId)
        // maybeSingle expresses directly what the PGRST116 special-case below
        // was working around: zero rows is a legitimate answer here.
        .maybeSingle();

      if (error) throw error;
      return (data as unknown as Submission) ?? null;
    },
    enabled: !!submissionId,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ============================================================================
// Canonical score event (the rich IRS breakdown behind a scored submission)
// ============================================================================

export interface ScoreEventComposite {
  finalScore: number;
  fourAxis: { ventureQuality: number; readiness: number; risk: number; evidenceQuality: number };
  ventureLabel: 'critical' | 'weak' | 'moderate' | 'strong' | 'excellent';
  evidenceGrade: 'high' | 'moderate' | 'low';
  dealReadiness: 'not-ready' | 'developing' | 'nearly-ready' | 'ready';
}

export interface ScoreEventDimension {
  id: string;
  rawScore: number;
  evidenceLevel: 'verified' | 'partially-verified' | 'unverified';
  confidence: number;
  feedback?: string;
}

export interface ScoreEventRisk {
  category: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
}

export interface SubmissionScoreEvent {
  composite: ScoreEventComposite;
  dimensions: ScoreEventDimension[];
  risks: ScoreEventRisk[];
  computedAt: string;
}

/**
 * Fetch the latest canonical `score_event` for a submission's promoted startup
 * profile. This is the rich breakdown (composite + per-dimension + granular
 * risks) — reachable by the investor who received the deck through the
 * submissions route of investor_score_events (quotes, sources and claims only
 * with a data-room grant).
 *
 * Returns null when there's no linked profile (unscorable) or no event yet.
 */
export function useSubmissionScoreEvent(startupProfileId: string | null | undefined) {
  return useQuery({
    queryKey: ['submission-score-event', startupProfileId ?? ''],
    queryFn: async (): Promise<SubmissionScoreEvent | null> => {
      if (!startupProfileId) return null;
      if (mode.isMock) return null;
      if (!isSupabaseConfigured()) return null;

      // The investor read path — investors have no SELECT on the raw row
      // (20261018130000); proof material follows the data-room grant.
      const [data] = await fetchInvestorScoreEvents([startupProfileId], { latestOnly: true });
      if (!data) return null;

      return {
        composite: data.composite as ScoreEventComposite,
        dimensions: (data.dimensions ?? []) as ScoreEventDimension[],
        risks: (data.risks ?? []) as ScoreEventRisk[],
        computedAt: data.computed_at as string,
      };
    },
    enabled: !!startupProfileId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// React Query Hooks — Mutations
// ============================================================================

/**
 * Create a new submission link
 */
export function useCreateSubmissionLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      investorId: string;
      slug: string;
      thesisMessage?: string;
      isActive?: boolean;
    }) => {
      if (mode.isMock) {
        return {
          ...getMockSubmissionLink(),
          slug: params.slug,
          thesis_message: params.thesisMessage ?? null,
          is_active: params.isActive ?? true,
        };
      }

      // Edge function's Zod schema (createSubmissionLinkSchema) expects
      // camelCase keys. The previous snake_case body silently lost
      // thesis_message + is_active because Zod strips unknown keys.
      const body = buildCreateLinkBody({
        slug: params.slug,
        thesisMessage: params.thesisMessage,
        isActive: params.isActive,
      });
      const { data, error } = await callSubmissionLinkAPI<CreateLinkEnvelope>(
        'POST',
        undefined,
        body,
      );

      if (error) throw new Error(error);
      const link = unwrapCreateResponse(data);
      if (!link) throw new Error('Create succeeded but response was missing the link payload');
      return link;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.submissionLinks(variables.investorId), [data]);
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissionLinks(variables.investorId),
      });
    },
  });
}

/**
 * Update an existing submission link
 */
export function useUpdateSubmissionLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      investorId: string;
      linkId: string;
      slug?: string;
      thesisMessage?: string;
      isActive?: boolean;
    }) => {
      if (mode.isMock) {
        // Persist the active state so getMockSubmissionLink() (used in the
        // useSubmissionLinks queryFn) returns the updated value after
        // invalidation triggers a refetch — prevents the toggle snapping back.
        if (params.isActive !== undefined) {
          _mockSubmissionLinkActive = params.isActive;
        }
        return {
          ...getMockSubmissionLink(),
          id: params.linkId,
          slug: params.slug ?? getMockSubmissionLink().slug,
          thesis_message: params.thesisMessage ?? getMockSubmissionLink().thesis_message,
          is_active: params.isActive ?? getMockSubmissionLink().is_active,
        };
      }

      // The edge function reads linkId from the query string (`?id=...`),
      // NOT from the body. Previously the hook sent id in the body, where
      // Zod silently stripped it and the handler then 400'd on "Missing
      // required query parameter: id" — meaning UPDATE has never worked.
      // Fix: pass linkId via the query, send only the patch fields in body.
      const body = buildUpdateLinkBody({
        slug: params.slug,
        thesisMessage: params.thesisMessage,
        isActive: params.isActive,
      });

      const { data, error } = await callSubmissionLinkAPI<CreateLinkEnvelope>(
        'PATCH',
        { id: params.linkId },
        body,
      );

      if (error) throw new Error(error);
      const link = unwrapCreateResponse(data);
      if (!link) throw new Error('Update succeeded but response was missing the link payload');
      return link;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.submissionLinks(variables.investorId) });
      const previous = queryClient.getQueryData(queryKeys.submissionLinks(variables.investorId));
      queryClient.setQueryData(
        queryKeys.submissionLinks(variables.investorId),
        (old: SubmissionLink[] | undefined) =>
          (old ?? []).map((l) =>
            l.id === variables.linkId ? { ...l, is_active: variables.isActive ?? l.is_active } : l
          )
      );
      return { previous };
    },
    onError: (_err, variables, context: { previous: unknown } | undefined) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.submissionLinks(variables.investorId), context.previous);
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissionLinks(variables.investorId),
      });
    },
  });
}

/**
 * Delete a submission link
 */
export function useDeleteSubmissionLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { investorId: string; linkId: string }) => {
      if (mode.isMock) {
        return { success: true };
      }

      // Edge function reads linkId from query (`?id=...`), same pattern as PATCH.
      const { error } = await callSubmissionLinkAPI<{ success: boolean }>(
        'DELETE',
        { id: params.linkId },
      );

      if (error) throw new Error(error);
      return { success: true };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissionLinks(variables.investorId),
      });
    },
  });
}

/**
 * Update submission status (e.g., pending -> archived, scored -> rejected)
 */
export function useUpdateSubmissionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      submissionId: string;
      investorId: string;
      status: SubmissionStatus;
    }) => {
      if (mode.isMock) {
        const mocks = getMockSubmissions();
        const found = mocks.find((s) => s.id === params.submissionId);
        return found ? { ...found, status: params.status } : null;
      }

      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await db
        .from('submissions')
        .update({ status: params.status })
        .eq('id', params.submissionId)
        .eq('investor_user_id', params.investorId)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as Submission;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissions(variables.investorId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissionDetail(variables.submissionId),
      });
    },
  });
}

/**
 * Shortlist a submission → move it into the investor's dealflow.
 *
 * Delegates to the `shortlist` action of the `submission-action` edge function.
 *
 * WHY SERVER-SIDE. This used to be three independent client writes: flip the
 * submission status, resolve the dealflow FKs, upsert dealflow. When FK
 * resolution failed it reported a Sentry warning, returned
 * `dealflowLinked: false`, and the caller never inspected the flag. But the
 * status had ALREADY flipped to 'shortlisted' — a TERMINAL status hidden from
 * the active inbox — so the deal disappeared from the inbox and never arrived in
 * dealflow. Silent, permanent deal loss.
 *
 * Two production deals were lost exactly this way ('logbook' and 'finai', both
 * 2026-06-11: no company_directory row, therefore no dealflow row, invisible on
 * both surfaces).
 *
 * The edge function orders the writes so that cannot happen — repair/resolve the
 * company_directory row, write dealflow, and only THEN flip the status — and
 * self-heals a missing directory row via the same `promoteToDirectory` the
 * scoring pipeline uses, instead of giving up. Any failure before the status
 * flip leaves the submission in the inbox with a real error surfaced, so a retry
 * is safe and idempotent.
 *
 * Un-shortlist and reject intentionally don't touch dealflow: once a company is
 * in the pipeline the investor controls its lifecycle from the dealflow screen,
 * not by manipulating the inbox.
 */
export function useShortlistSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      submissionId: string;
      investorId: string;
      /** 119-07: the investor confirmed it is NOT the closed company it matched (explicit, logged server-side). */
      confirmNoConflict?: boolean;
    }) => {
      if (mode.isMock) {
        const mocks = getMockSubmissions();
        const found = mocks.find((s) => s.id === params.submissionId);
        return {
          submission: found
            ? ({ ...found, status: 'shortlisted' as const, expires_at: null } as unknown as Submission)
            : null,
          startupProfileId: null,
          dealflowLinked: false,
        };
      }

      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase.functions.invoke('submission-action', {
        body: {
          action: 'shortlist',
          submissionId: params.submissionId,
          ...(params.confirmNoConflict === true ? { confirm_no_conflict: true } : {}),
        },
      });

      // The function returns a real error when anything fails BEFORE the status
      // flip, and leaves the submission in the inbox. Throwing here means the
      // caller shows it — the old code's silent `dealflowLinked: false` is gone.
      // Typed (119-07): a deck matching a closed company answers 409
      // matched_closed_company with the card, and the screen asks the investor.
      if (error) throw await submissionActionError(error, 'shortlist');
      if (!data?.success) {
        throw new Error(data?.error ?? 'Could not shortlist this submission');
      }

      return {
        submission: null,
        startupProfileId: (data.startupProfileId as string | null) ?? null,
        dealflowLinked: true,
      };
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissions(variables.investorId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissionDetail(variables.submissionId),
      });
      // Dealflow reads through `useDealflowFromSupabase`, whose key is
      // `['dealflow', investor_profiles.id, 'enriched']` — NOT the auth user id.
      // The original code invalidated `dealflow(<auth user id>)`, which never
      // prefix-matched the enriched key, so even a successfully-written deal
      // stayed invisible until a cold reload. The investor_profiles.id is no
      // longer returned to the client (the edge function owns that resolution),
      // so invalidate the whole 'dealflow' scope — it prefix-matches every
      // variant and costs one refetch on an action a user takes rarely.
      queryClient.invalidateQueries({ queryKey: ['dealflow'] });
    },
  });
}

/**
 * The investor confirms a HELD deck is a different venture from the closed
 * company it matched (119-03): the server creates a NEW private profile and
 * scores it in the background (202, status 'scoring'). The inbox's polling
 * picks the result up. Dismissing instead is the existing archive, which
 * scores nothing.
 */
export function useConfirmDifferentCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { submissionId: string; investorId: string }) => {
      if (mode.isMock || !isSupabaseConfigured()) {
        throw new SubmissionActionError('failed', 'This needs a live account. It is not available in sample mode.');
      }
      const { data, error } = await supabase.functions.invoke('submission-action', {
        body: { action: 'confirm-different-company', submissionId: params.submissionId, confirm_no_conflict: true },
      });
      if (error) throw await submissionActionError(error, 'confirm-different-company');
      if (!data?.success) throw new SubmissionActionError('failed', SUBMISSION_ACTION_GENERIC, { detail: 'no_success' });
      return { status: (data.status as string | undefined) ?? 'scoring' };
    },
    onSuccess: (_r, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions(variables.investorId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.submissionDetail(variables.submissionId) });
    },
  });
}

// ============================================================================
// Mock Data Generators
// ============================================================================

/**
 * Module-level mutable flag for mock/demo mode: tracks whether the mock
 * submission link is active. Toggling via useUpdateSubmissionLink in mock
 * mode sets this flag so subsequent reads from getMockSubmissionLink() and
 * the useSubmissionLinks queryFn return the updated state instead of always
 * snapping back to true.
 */
let _mockSubmissionLinkActive = true;

/** @internal — exported only for tests */
export function _resetMockSubmissionLinkActive() {
  _mockSubmissionLinkActive = true;
}

/**
 * Generate a mock submission link for demo/mock mode
 */
export function getMockSubmissionLink(): SubmissionLink {
  return {
    id: 'mock-link-001',
    investor_user_id: 'mock-investor-001',
    slug: 'marc-dubois',
    is_active: _mockSubmissionLinkActive,
    thesis_message:
      'I invest in seed-stage B2B SaaS companies in Europe. Looking for strong teams with early traction.',
    max_storage_mb: 500,
    current_storage_bytes: 125_000_000,
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    submission_count: 12,
    url: 'https://inverio.ai/submit/marc-dubois',
  };
}

/**
 * Generate an array of realistic mock submissions for demo/mock mode.
 *
 * Distribution: ~40% scored, ~20% pending/scoring, ~15% shortlisted,
 * ~15% archived, ~10% rejected.
 */
export function getMockSubmissions(count: number = 12): Submission[] {
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;

  const companies: Array<{
    name: string;
    email: string;
    contactName: string | null;
    sector: string;
    stage: string;
    oneLiner: string;
    askAmount: number | null;
    teamSize: number | null;
    status: SubmissionStatus;
    score: number | null;
    confidence: number | null;
    daysAgo: number;
    expiresInDays: number | null;
  }> = [
    {
      name: 'DataWeave AI',
      email: 'pitch@dataweave.ai',
      contactName: 'Sophie Leclerc',
      sector: 'AI/ML',
      stage: 'seed',
      oneLiner: 'AI-powered data integration for enterprise ETL pipelines',
      askAmount: 3_000_000,
      teamSize: 8,
      status: 'scored',
      score: 82,
      confidence: 88,
      daysAgo: 2,
      expiresInDays: 58,
    },
    {
      name: 'CloudKitchen Pro',
      email: 'founders@cloudkitchenpro.com',
      contactName: 'Marcus Weber',
      sector: 'FoodTech',
      stage: 'series-a',
      oneLiner: 'Ghost kitchen management platform for restaurant chains',
      askAmount: 8_000_000,
      teamSize: 22,
      status: 'scored',
      score: 71,
      confidence: 79,
      daysAgo: 5,
      expiresInDays: 55,
    },
    {
      name: 'PayVault',
      email: 'team@payvault.io',
      contactName: null,
      sector: 'FinTech',
      stage: 'seed',
      oneLiner: 'Secure payment orchestration for emerging markets',
      askAmount: 2_500_000,
      teamSize: 6,
      status: 'shortlisted',
      score: 88,
      confidence: 92,
      daysAgo: 7,
      expiresInDays: null,
    },
    {
      name: 'NexGen Robotics',
      email: 'info@nexgenrobotics.ch',
      contactName: 'Anna Mueller',
      sector: 'DeepTech',
      stage: 'series-a',
      oneLiner: 'Autonomous warehouse robots for mid-size logistics',
      askAmount: 12_000_000,
      teamSize: 35,
      status: 'scored',
      score: 76,
      confidence: 83,
      daysAgo: 3,
      expiresInDays: 57,
    },
    {
      name: 'LearnLoop',
      email: 'hello@learnloop.app',
      contactName: 'James Okafor',
      sector: 'EdTech',
      stage: 'pre-seed',
      oneLiner: 'Adaptive micro-learning platform for corporate training',
      askAmount: 750_000,
      teamSize: 4,
      status: 'pending',
      score: null,
      confidence: null,
      daysAgo: 1,
      expiresInDays: 59,
    },
    {
      name: 'SwissGreen Tech',
      email: 'pitch@swissgreen.tech',
      contactName: 'Lena Brunner',
      sector: 'CleanTech',
      stage: 'seed',
      oneLiner: 'Carbon-negative building materials from recycled concrete',
      askAmount: 4_000_000,
      teamSize: 11,
      status: 'scored',
      score: 68,
      confidence: 74,
      daysAgo: 10,
      expiresInDays: 50,
    },
    {
      name: 'MedFlow',
      email: 'contact@medflow.health',
      contactName: 'Dr. Eva Novak',
      sector: 'HealthTech',
      stage: 'seed',
      oneLiner: 'Clinical workflow automation for outpatient clinics',
      askAmount: 2_000_000,
      teamSize: 9,
      status: 'shortlisted',
      score: 85,
      confidence: 90,
      daysAgo: 14,
      expiresInDays: null,
    },
    {
      name: 'CryptoLedger',
      email: 'biz@cryptoledger.io',
      contactName: null,
      sector: 'FinTech',
      stage: 'seed',
      oneLiner: 'Institutional-grade crypto accounting and tax compliance',
      askAmount: 5_000_000,
      teamSize: 14,
      status: 'archived',
      score: 52,
      confidence: 65,
      daysAgo: 25,
      expiresInDays: null,
    },
    {
      name: 'FarmSense',
      email: 'invest@farmsense.io',
      contactName: 'Raj Patel',
      sector: 'AgriTech',
      stage: 'pre-seed',
      oneLiner: 'IoT soil sensors with AI-driven crop yield predictions',
      askAmount: 600_000,
      teamSize: 3,
      status: 'scoring',
      score: null,
      confidence: null,
      daysAgo: 1,
      expiresInDays: 59,
    },
    {
      name: 'UrbanPulse',
      email: 'founders@urbanpulse.city',
      contactName: 'Mia Chang',
      sector: 'PropTech',
      stage: 'seed',
      oneLiner: 'Smart city analytics platform for urban planners',
      askAmount: 3_500_000,
      teamSize: 10,
      status: 'rejected',
      score: 38,
      confidence: 55,
      daysAgo: 20,
      expiresInDays: null,
    },
    {
      name: 'TalentForge',
      email: 'hello@talentforge.ai',
      contactName: 'Oliver Fischer',
      sector: 'HR Tech',
      stage: 'seed',
      oneLiner: 'AI recruiter copilot for technical hiring pipelines',
      askAmount: 2_000_000,
      teamSize: 7,
      status: 'scored',
      score: 74,
      confidence: 81,
      daysAgo: 8,
      expiresInDays: 52,
    },
    {
      name: 'LogiChain',
      email: 'deck@logichain.co',
      contactName: 'Sara Kim',
      sector: 'Supply Chain',
      stage: 'series-a',
      oneLiner: 'Real-time supply chain visibility for cross-border trade',
      askAmount: 10_000_000,
      teamSize: 28,
      status: 'archived',
      score: 61,
      confidence: 70,
      daysAgo: 28,
      expiresInDays: null,
    },
  ];

  return companies.slice(0, count).map((c, idx) => {
    const createdAt = new Date(now - c.daysAgo * DAY);
    const scoredAt = c.score !== null ? new Date(now - (c.daysAgo - 1) * DAY) : null;
    const expiresAt = c.expiresInDays !== null ? new Date(now + c.expiresInDays * DAY) : null;

    const extractedData: SubmissionExtractedData | null = {
      sector: c.sector,
      stage: c.stage,
      one_liner: c.oneLiner,
      ask_amount: c.askAmount,
      team_size: c.teamSize,
    };

    return {
      id: `mock-sub-${String(idx + 1).padStart(3, '0')}`,
      link_id: 'mock-link-001',
      investor_user_id: 'mock-investor-001',
      company_name: c.name,
      contact_email: c.email,
      contact_name: c.contactName,
      deck_storage_path: c.score !== null ? `submissions/mock-investor-001/${c.name.toLowerCase().replace(/\s/g, '-')}.pdf` : null,
      deck_size_bytes: 1_500_000 + Math.floor(Math.random() * 3_500_000),
      ai_score: c.score,
      ai_confidence: c.confidence,
      ai_extracted_data: extractedData,
      // Mock rows carry no real score_event; the modal falls back to the
      // simple score card. Mock mode short-circuits the score-event fetch.
      startup_profile_id: null,
      status: c.status,
      claimed_by: null,
      score_visible_to_marketplace: c.status === 'shortlisted',
      consent_given: true,
      created_at: createdAt.toISOString(),
      scored_at: scoredAt?.toISOString() ?? null,
      expires_at: expiresAt?.toISOString() ?? null,
    };
  });
}
